import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { Upload, File, Loader2, AlertCircle, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { RichTextField } from './RichTextField';
import { ProjectTagsField } from './ProjectTagsField';
import { AnalyzeButton } from './AnalyzeButton';
import { TierLimitModal } from './TierLimitModal';
import { wouldExceedStorageQuota } from '../utils/storage-quota';

function stripHtml(html: string): string {
  if (!html || !html.trim()) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
}

function isMeaningfulText(html: string): boolean {
  const s = stripHtml(html);
  if (!s) return false;
  const emptyTags = ['<p></p>', '<p><br></p>', '<p><br/></p>'];
  const trimmed = (html ?? '').trim();
  if (emptyTags.includes(trimmed)) return false;
  return true;
}

interface FileWithProgress {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  resourceId?: string;
}

interface ImportTimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess: () => void;
}

export const ImportTimelineModal: React.FC<ImportTimelineModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onSuccess,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [text, setText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const resourceLimitDispatchedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setText('');
      setTags([]);
      setFiles([]);
      setError(null);
      setIsAnalyzing(false);
      resourceLimitDispatchedRef.current = false;
    }
  }, [isOpen]);

  const addFiles = useCallback((fileList: FileList | null): void => {
    if (!fileList) return;
    const newFiles: FileWithProgress[] = Array.from(fileList).map((file) => ({
      file,
      id: `${Date.now()}-${Math.random()}`,
      status: 'pending' as const,
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (id: string): void => {
    if (isAnalyzing) return;
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadOneFile = async (
    fileWithProgress: FileWithProgress,
    onProgress: (id: string, status: FileWithProgress['status'], progress: number, resourceId?: string, error?: string) => void
  ): Promise<string | null> => {
    if (!token) return null;
    onProgress(fileWithProgress.id, 'uploading', 0);
    try {
      const presignedRes = await fetch(
        `${API_BASE}/api/projects/${projectId}/resources/presigned-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: fileWithProgress.file.name,
            fileSize: fileWithProgress.file.size,
            mimeType: fileWithProgress.file.type || 'application/octet-stream',
          }),
        }
      );
      if (!presignedRes.ok) throw new Error('Failed to get upload URL');
      const { presignedUrl, s3Key } = await presignedRes.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            onProgress(fileWithProgress.id, 'uploading', pct);
          }
        });
        xhr.addEventListener('load', () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed ${xhr.status}`))));
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', fileWithProgress.file.type || 'application/octet-stream');
        xhr.send(fileWithProgress.file);
      });

      const saveRes = await fetch(`${API_BASE}/api/projects/${projectId}/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: fileWithProgress.file.name,
          fileSize: fileWithProgress.file.size,
          mimeType: fileWithProgress.file.type || 'application/octet-stream',
          s3Key,
          tags: ['scribane: timeline'],
        }),
      });
      if (!saveRes.ok) {
        const errData = await saveRes.json().catch(() => ({}));
        if (errData.errorCode === 'RESOURCE_LIMIT_REACHED') throw new Error('RESOURCE_LIMIT_REACHED');
        throw new Error(await saveRes.text().catch(() => 'Failed to save resource'));
      }
      const saveData = await saveRes.json();
      onProgress(fileWithProgress.id, 'success', 100, saveData.id);
      return saveData.id as string;
    } catch (err) {
      if (err instanceof Error && err.message === 'RESOURCE_LIMIT_REACHED') {
        if (!resourceLimitDispatchedRef.current) {
          resourceLimitDispatchedRef.current = true;
          window.dispatchEvent(new CustomEvent('resource-limit-reached'));
        }
        onProgress(fileWithProgress.id, 'error', 0, undefined, 'Resource limit reached');
        return null;
      }
      const msg = err instanceof Error ? err.message : 'Upload failed';
      onProgress(fileWithProgress.id, 'error', 0, undefined, msg);
      return null;
    }
  };

  const handleAnalyze = async (): Promise<void> => {
    if (!token || !projectId) {
      setError(t('not_authenticated'));
      return;
    }
    const hasText = isMeaningfulText(text);
    const hasFiles = files.length > 0;
    if (!hasText && !hasFiles) return;

    setIsAnalyzing(true);
    setError(null);

    const resourceIds: string[] = [];
    const pendingFiles = files.filter((f) => f.status === 'pending');

    if (pendingFiles.length > 0) {
      const totalSize = pendingFiles.reduce((sum, f) => sum + f.file.size, 0);
      const exceedsQuota = await wouldExceedStorageQuota(token, totalSize);
      if (exceedsQuota) {
        setShowStorageLimitModal(true);
        setIsAnalyzing(false);
        return;
      }
      const updateFile = (id: string, status: FileWithProgress['status'], progress: number, resourceId?: string, err?: string) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === id ? { ...f, status, progress, ...(resourceId && { resourceId }), ...(err && { error: err }) } : f
          )
        );
      };
      for (const f of pendingFiles) {
        const id = await uploadOneFile(f, updateFile);
        if (id) resourceIds.push(id);
      }
    } else {
      files.filter((f): f is FileWithProgress & { resourceId: string } => f.status === 'success' && !!f.resourceId).forEach((f) => resourceIds.push(f.resourceId));
    }

    const plainText = hasText ? stripHtml(text) : '';
    if (!plainText && resourceIds.length === 0) {
      setError(t('timeline_import_no_content'));
      setIsAnalyzing(false);
      return;
    }

    try {
      const importRes = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-items/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text: plainText || undefined,
          resourceIds: resourceIds.length > 0 ? resourceIds : undefined,
          tags: tags.length > 0 ? tags : undefined,
        }),
      });
      if (!importRes.ok) {
        const data = await importRes.json().catch(() => ({}));
        throw new Error(data.error || t('timeline_import_failed'));
      }
      const data = await importRes.json();
      const createdCount = typeof data.created === 'number' ? data.created : 0;
      if (createdCount > 0) {
        onSuccess();
        onClose();
      } else {
        setError(t('timeline_import_no_events'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('timeline_import_failed'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const hasContent = isMeaningfulText(text) || files.length > 0;
  const canAnalyze = hasContent && !isAnalyzing;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && !isAnalyzing && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('timeline_import_title')}</DialogTitle>
            <DialogDescription>{t('timeline_import_description')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('timeline_import_text_label')}</label>
              <RichTextField
                value={text}
                onChange={setText}
                placeholder={t('timeline_import_text_placeholder')}
                minHeight="120px"
                maxHeight="30vh"
                autoFocus
              />
            </div>
            <ProjectTagsField
              projectId={projectId}
              tags={tags}
              onTagsChange={setTags}
              disabled={isAnalyzing}
              contentClassName="z-[2010]"
            />
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('timeline_import_files_label')}</label>
              <div
                ref={dropZoneRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all border-muted-foreground/25 hover:border-muted-foreground/50"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">{t('Drag and drop files here')}</p>
                <p className="text-xs text-muted-foreground">{t('or click to select files')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
            {files.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t('Selected files')} ({files.length})</p>
                <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto">
                  {files.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center gap-3 p-2 border rounded-md bg-muted/30"
                    >
                      <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{f.file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(f.file.size)}</p>
                        {f.status === 'uploading' && (
                          <div className="mt-1 w-full h-1 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${f.progress}%` }} />
                          </div>
                        )}
                        {f.status === 'error' && f.error && (
                          <p className="text-xs text-destructive">{f.error}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {f.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin" />}
                        {!isAnalyzing && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeFile(f.id)}
                            aria-label={t('remove')}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <CancelButton type="button" onClick={onClose} disabled={isAnalyzing} />
            <AnalyzeButton
              onClick={() => void handleAnalyze()}
              isAnalyzing={isAnalyzing}
              disabled={!canAnalyze}
              dialogPrimary
              variant="default"
            >
              <>
                {t('analyze')}
                {!isAnalyzing && <DialogPrimaryShortcut className="ml-1.5" />}
              </>
            </AnalyzeButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TierLimitModal open={showStorageLimitModal} onOpenChange={setShowStorageLimitModal} limitType="storage" />
    </>
  );
};
